import Badge from '@mui/material/Badge';
import Chip from '@mui/material/Chip';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Tooltip from '@mui/material/Tooltip';
import { useAtom, useAtomValue } from 'jotai';
import { useMemo } from 'react';
import { NavLink } from 'react-router';

import {
  organizationsAtom,
  selectedOrganizationAtom
} from '@/pages/Organizations';
import { newReportsCountAtom } from '@/pages/Reports/Reports.store';
import { openTicketsCountAtom } from '@/pages/Tickets/Tickets.store';
import { isLayoutMobileAtom } from '@/store/layout';
import { isSidebarExpandedAtom, isSidebarOpenAtom } from '@/store/sidebar';
import { userIsInternalAtom } from '@/store/user.store';

import useStyles from '../Sidebar.styles';
import type { SidebarMenuItemProps } from '../SidebarSubmenuButton/SidebarSubmenuButton.types';
import type { SidebarButtonProps } from './SidebarRouteButton.types';

function SidebarRouteButton(props: SidebarButtonProps) {
  const { item } = props;
  const [isLayoutMobile] = useAtom(isLayoutMobileAtom);
  const [newReportsCount] = useAtom(newReportsCountAtom);
  const [organizations] = useAtom(organizationsAtom);
  const [, setIsSidebarOpen] = useAtom(isSidebarOpenAtom);
  const [isSidebarExpanded] = useAtom(isSidebarExpandedAtom);
  const [userIsInternal] = useAtom(userIsInternalAtom);
  const [selectedOrganization] = useAtom(selectedOrganizationAtom);
  const openTicketsCount = useAtomValue(openTicketsCountAtom);

  const { classes } = useStyles({
    isLayoutMobile,
    isSidebarExpanded,
    organizationsLengthEqualToOne: organizations.length === 1
  });

  const getItemNavPath = (navItem: SidebarMenuItemProps) => {
    if (navItem.pathTo === undefined) {
      return '/';
    }
    if (navItem.label === 'Cases') {
      return '/cases';
    }
    if (navItem.label === 'Vulnerability Management') {
      // Redirect to the vulnerabilities page with a default filter for unremediated vulnerabilities
      const search = new URLSearchParams({
        f: JSON.stringify({
          date_remediated: ['__isnull', true]
        })
      });

      return `/vulnerability-management/vulnerabilities?${search.toString()}`;
    }
    return navItem.pathTo;
  };

  const reportsCount = useMemo(() => {
    if (isSidebarExpanded || isLayoutMobile) {
      return <Chip label={newReportsCount} size="small" color="warning" />;
    }
    if (!isSidebarExpanded) {
      return (
        <Badge
          className={classes.sidebarButtonBadge}
          badgeContent={newReportsCount}
          color="warning"
        />
      );
    }
    return null;
  }, [
    isLayoutMobile,
    isSidebarExpanded,
    newReportsCount,
    selectedOrganization,
    userIsInternal
  ]);

  const ticketsCount = useMemo(() => {
    if (isSidebarExpanded || isLayoutMobile) {
      return <Chip label={openTicketsCount} size="small" color="warning" />;
    }
    if (!isSidebarExpanded) {
      return (
        <Badge
          className={classes.sidebarButtonBadge}
          badgeContent={openTicketsCount}
          color="warning"
        />
      );
    }
    return null;
  }, [
    isLayoutMobile,
    isSidebarExpanded,
    openTicketsCount,
    selectedOrganization,
    userIsInternal
  ]);

  const handleClick = () => {
    if (isLayoutMobile) {
      setIsSidebarOpen(false);
    }
  };

  return (
    <Tooltip
      arrow
      placement="right"
      title={!isSidebarExpanded ? item.label : null}
      classes={{
        arrow: classes.sidebarButtonTooltipArrow,
        tooltip: classes.sidebarButtonTooltip
      }}
      slotProps={{
        popper: {
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, -5]
              }
            }
          ]
        }
      }}
    >
      <ListItem className={classes.sidebarButton} disablePadding>
        <NavLink
          aria-label={item.ariaLabel}
          className={classes.sidebarButtonNavLink}
          to={getItemNavPath(item) as string}
          onClick={handleClick}
        >
          <ListItemButton className={classes.sidebarButtonButton}>
            <div>
              <ListItemIcon className={classes.sidebarButtonIcon}>
                {item.component}
              </ListItemIcon>
              <ListItemText
                className={classes.sidebarButtonText}
                primary={item.label}
              />
            </div>
            {item.label === 'Reports' && newReportsCount > 0 && reportsCount}
            {item.label === 'Tickets' && openTicketsCount > 0 && ticketsCount}
          </ListItemButton>
        </NavLink>
      </ListItem>
    </Tooltip>
  );
}

export default SidebarRouteButton;
